import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import type { TranslationJobResponse } from 'types/AiHubTranslation';

type AppliedState = {
  postText: boolean;
  firstComment: boolean;
  images: boolean;
};

export const useTranslationAppliedState = (
  postId: number,
  translationJob?: TranslationJobResponse,
): AppliedState => {
  const { data: postDetail } = useQuery(queryKey.postDetail(postId), () =>
    PostApi.detail(postId),
  );

  return useMemo(() => {
    if (!postDetail || !translationJob?.fields) {
      return { postText: false, firstComment: false, images: false };
    }

    const postTextApplied =
      !!translationJob.fields.postText?.translated &&
      postDetail.raw_text === translationJob.fields.postText.translated;

    const firstCommentApplied =
      !!translationJob.fields.firstComment?.translated &&
      postDetail.attachments?.firstComment?.text ===
        translationJob.fields.firstComment.translated;

    const imagesApplied =
      !!translationJob.fields.images?.length &&
      translationJob.fields.images.filter(
        (img) =>
          img.status === 'completed' &&
          img.translatedMediumId != null &&
          img.cardIndex != null,
      ).length > 0 &&
      translationJob.fields.images
        .filter(
          (img) =>
            img.status === 'completed' &&
            img.translatedMediumId != null &&
            img.cardIndex != null,
        )
        .every((img) => {
          const card = postDetail.attachments?.cards?.[img.cardIndex!];
          return card?.medium?.id === img.translatedMediumId;
        });

    return {
      postText: postTextApplied,
      firstComment: firstCommentApplied,
      images: imagesApplied,
    };
  }, [postDetail, translationJob]);
};
